import React, { useState } from 'react';
import { Button, Menu, MenuItem, Tooltip } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import { contactViaWhatsApp } from '../../utils/whatsappUtils';
import { shareImageToWhatsApp, downloadImage } from '../../utils/imageShareUtils';

/**
 * WhatsApp Contact Button Component
 * A reusable button that redirects to WhatsApp with product details
 * 
 * @param {Object} props - Component props
 * @param {Object} props.product - Product details to send via WhatsApp
 * @param {string} [props.buttonText="Contact via WhatsApp"] - Button text
 * @param {Object} [props.buttonProps] - Additional props for the Button component
 * @param {string} [props.variant="contained"] - Button variant
 * @param {string} [props.color="success"] - Button color
 * @param {boolean} [props.fullWidth=false] - Whether the button should take full width
 * @param {string} [props.size="medium"] - Button size
 * @param {boolean} [props.showShareOptions=false] - Whether to show image sharing options
 */
const WhatsAppButton = ({ 
  product, 
  buttonText = "Contact via WhatsApp", 
  buttonProps = {},
  variant = "contained",
  color = "success",
  fullWidth = false,
  size = "medium",
  showShareOptions = true
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  const handleClick = (event) => {
    if (showShareOptions && product.image) {
      setAnchorEl(event.currentTarget);
    } else {
      contactViaWhatsApp(product);
    }
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleContactWithText = () => {
    contactViaWhatsApp(product);
    handleClose();
  };
  
  const handleShareImage = () => {
    const text = `I'm interested in this ${product.name}${product.price ? ` priced at ${product.price}` : ''}`;
    shareImageToWhatsApp(product.image, text);
    handleClose();
  };
  
  const handleDownloadImage = () => {
    const fileName = product.name.replace(/\s+/g, '-').toLowerCase() + '.jpg';
    downloadImage(product.image, fileName);
    handleClose();
  };

  return (
    <>
      <Tooltip title="Contact us about this product">
        <Button
          variant={variant}
          color={color}
          startIcon={<WhatsAppIcon />}
          onClick={handleClick}
          fullWidth={fullWidth}
          size={size}
          {...buttonProps}
        >
          {buttonText}
        </Button>
      </Tooltip>
      
      {showShareOptions && product.image && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <MenuItem onClick={handleContactWithText}>
            <WhatsAppIcon fontSize="small" style={{ marginRight: 8 }} />
            Send product details
          </MenuItem>
          <MenuItem onClick={handleShareImage}>
            <ShareIcon fontSize="small" style={{ marginRight: 8 }} />
            Share with image
          </MenuItem>
          <MenuItem onClick={handleDownloadImage}>
            <DownloadIcon fontSize="small" style={{ marginRight: 8 }} />
            Download image
          </MenuItem>
        </Menu>
      )}
    </>
  );
};

export default WhatsAppButton; 